// src/views/round.ts
import { renderSwatch } from '../ui/swatch';
import { renderSlider, wireSliders } from '../ui/slider';
import { hslToHex } from '../core/color';
import type { Hsl, Puzzle, RoundResult, ChallengeContext } from '../types';

const COMMIT_RECEIPT_MS = 900;
function prefersReducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

type RoundOpts = {
  puzzle: Puzzle;
  roundIdx: 0 | 1 | 2 | 3 | 4;
  initial: Hsl;
  showHint: boolean;
  challenge?: ChallengeContext | null;
  onSubmit: (guess: Hsl) => void;
};

export function renderRound(root: HTMLElement, opts: RoundOpts): void {
  const target = opts.puzzle.targets[opts.roundIdx];
  let guess: Hsl = { ...opts.initial };
  const hint = opts.showHint
    ? `<p class="first-round-hint">Match the color with the three sliders. Five rounds, one share.</p>`
    : '';
  const callout = opts.challenge
    ? `<p class="challenge-callout">A friend scored <strong>${Number(opts.challenge.score)}</strong> on today’s puzzle. Beat them.</p>`
    : '';

  root.innerHTML = `
    <header class="app-header">
      <span class="brand">Barve №${opts.puzzle.dayNumber}</span>
      <span class="round-counter">${roman(opts.roundIdx + 1)} ${progressDots(opts.roundIdx, opts.roundIdx)}</span>
    </header>
    <div class="card">
      ${hint}
      ${callout}
      <div class="target-guess-pair">
        ${renderSwatch(target, { variant: 'target', tag: 'TARGET' })}
        ${renderSwatch(guess, { variant: 'guess', tag: 'YOUR GUESS' })}
      </div>
      ${renderSlider('h', guess)}
      ${renderSlider('s', guess)}
      ${renderSlider('l', guess)}
      <footer class="card-foot">
        <button class="lock" id="submit" type="button">
          <span class="lock-word">lock it in</span>
          <span class="lock-mark" aria-hidden="true">→</span>
        </button>
      </footer>
    </div>
  `;

  const guessEl = root.querySelectorAll('.swatch')[1] as HTMLElement;
  wireSliders(root, guess, (next) => {
    guess = next;
    guessEl.style.background = `hsl(${guess.h}, ${guess.s}%, ${guess.l}%)`;
  });

  // Commit micro-interaction: on click, lock the button (the .committed class
  // doubles as double-click guard) and briefly swap the .lock-word text to the
  // hex of the submitted color before transitioning. The .lock-mark return
  // glyph rotates 90° and fades via CSS triggered by the same class. Tactile
  // receipt — "I heard you, here's what you committed." Timer lives within the
  // view's lifecycle; navigation drops the closure with the DOM.
  const submitBtn = root.querySelector('#submit') as HTMLButtonElement;
  const lockWordEl = submitBtn.querySelector('.lock-word') as HTMLElement;
  submitBtn.addEventListener('click', () => {
    if (submitBtn.classList.contains('committed')) return;
    submitBtn.disabled = true;
    const finalGuess: Hsl = { ...guess };
    submitBtn.classList.add('committed');

    // Build receipt: hex code + HSL breakdown + color dot
    lockWordEl.textContent = hslToHex(finalGuess);

    const hslLine = document.createElement('span');
    hslLine.className = 'receipt-hsl';
    hslLine.textContent = `${finalGuess.h}° ${finalGuess.s}% ${finalGuess.l}%`;

    const dot = document.createElement('span');
    dot.className = 'receipt-dot';
    dot.style.background = `hsl(${finalGuess.h}, ${finalGuess.s}%, ${finalGuess.l}%)`;

    submitBtn.insertBefore(dot, lockWordEl);
    submitBtn.appendChild(hslLine);

    const delay = prefersReducedMotion() ? 0 : COMMIT_RECEIPT_MS;
    setTimeout(() => opts.onSubmit(finalGuess), delay);
  });
}

type BetweenOpts = {
  puzzle: Puzzle;
  result: RoundResult;
  roundsDone: number;
  isLast: boolean;
  onNext: () => void;
};

// Qualitative feedback thresholds roughly track ΔE2000 bands via score:
//   ΔE ≤ 3  → score ~93  → "Imperceptible."
//   ΔE ≤ 7  → score ~79  → "Close."
//   ΔE ≤ 15 → score ~52  → "In the neighbourhood."
//   ΔE ≤ 25 → score ~18  → "Off."
//   else    → "Far."
function feedbackForScore(score: number): string {
  if (score >= 90) return 'Imperceptible.';
  if (score >= 75) return 'Close.';
  if (score >= 50) return 'In the neighbourhood.';
  if (score >= 15) return 'Off.';
  return 'Far.';
}

export function renderBetween(root: HTMLElement, opts: BetweenOpts): void {
  const { target, guess, score } = opts.result;
  const line = feedbackForScore(score);
  // The between screen never renders for round 5 (App routes round-5 submit
  // directly to finishGame → results), so isLast is effectively always false
  // here. Keep the prop + branch as defensive scaffolding in case mid-game
  // resume ever lands a 5-result state on this view.
  const action = opts.isLast ? 'results' : 'next';
  const done = opts.roundsDone;
  root.innerHTML = `
    <header class="app-header">
      <span class="brand">Barve №${opts.puzzle.dayNumber}</span>
      <span class="round-counter">${roman(done)} ${progressDots(done)}</span>
    </header>
    <div class="card">
      <div class="between-pair">
        ${renderSwatch(target, { variant: 'target', tag: 'TARGET' })}
        ${renderSwatch(guess, { variant: 'guess', tag: 'YOUR GUESS' })}
      </div>
      <p class="feedback-line">${line}</p>
      <footer class="card-foot">
        <button class="lock" id="next" type="button">
          <span class="lock-word">${action}</span>
          <span class="lock-mark" aria-hidden="true">→</span>
        </button>
      </footer>
    </div>
  `;
  root.querySelector('#next')!.addEventListener('click', opts.onNext);
}

function roman(n: number): string {
  return ['I','II','III','IV','V'][n - 1] ?? String(n);
}

/** Five small dots showing round progress. */
function progressDots(completedCount: number, activeDot?: number): string {
  const dots = Array.from({ length: 5 }, (_, i) => {
    const cls =
      i === activeDot ? 'dot active'
      : i < completedCount ? 'dot done'
      : 'dot';
    return `<span class="${cls}"></span>`;
  }).join('');
  return `<span class="round-dots" aria-hidden="true">${dots}</span>`;
}
